class USSFPlayerCarousel {
  constructor(section) {
    this.section = section;
    this.viewport = section.querySelector('.ussf-player-carousel__viewport');
    this.track = section.querySelector('.ussf-player-carousel__track');
    this.slides = Array.from(section.querySelectorAll('.ussf-player-carousel__slide'));
    this.prevBtn = section.querySelector('.ussf-player-carousel__arrow--prev');
    this.nextBtn = section.querySelector('.ussf-player-carousel__arrow--next');
    this.countEl = section.querySelector('.ussf-player-carousel__count');
    this.progressTrack = section.querySelector('.ussf-player-carousel__progress-track');

    if (!this.viewport || this.slides.length === 0) return;

    // Configure group size for desktop
    this.desktopGroupSize = parseInt(section.dataset.groupSize || '3', 10);
    this.breakpoint = 768;
    this.scrollTimeout = null;
    
    this.currentIndex = 0;
    this.isProgrammaticScroll = false;
    this.slideWidth = this.slides[0].offsetWidth;
    this.gap = this.getGap();

    // Initialize responsive group size
    this.updateGroupSize();
    
    // Generate initial pagination segments
    this.generatePaginationSegments();

    this.update();
    this.bindEvents();

    window.addEventListener('resize', () => this.onResize());
  }

  getGap() {
    const styles = window.getComputedStyle(this.track);
    return parseInt(styles.gap || styles.columnGap || 0, 10);
  }

  // Determine if we're on desktop (above breakpoint)
  isDesktop() {
    return window.innerWidth >= this.breakpoint;
  }

  // Update group size based on viewport
  updateGroupSize() {
    this.groupSize = this.isDesktop() ? this.desktopGroupSize : 1;
    this.totalGroups = Math.ceil(this.slides.length / this.groupSize);
  }

  // Generate pagination segments based on total groups
  generatePaginationSegments() {
    if (!this.progressTrack) return;

    // Clear existing segments
    this.progressTrack.innerHTML = '';

    // Create segments for each group
    for (let i = 0; i < this.totalGroups; i++) {
      const segment = document.createElement('span');
      segment.className = 'ussf-player-carousel__progress-segment';
      if (i === 0) {
        segment.classList.add('is-active');
      }
      this.progressTrack.appendChild(segment);
    }

    // Update segments reference
    this.segments = Array.from(
      this.progressTrack.querySelectorAll('.ussf-player-carousel__progress-segment')
    );
  }

  bindEvents() {
    this.nextBtn?.addEventListener('click', () => this.goNext());
    this.prevBtn?.addEventListener('click', () => this.goPrev());

    this.viewport.addEventListener('scroll', () => this.onScroll(), { passive: true });
  }

  onResize() {
    const wasDesktop = this.groupSize === this.desktopGroupSize;
    const isNowDesktop = this.isDesktop();
    
    // Update slide dimensions
    this.slideWidth = this.slides[0].offsetWidth;
    this.gap = this.getGap();
    
    // Check if viewport crossed the breakpoint
    if (wasDesktop !== isNowDesktop) {
      this.updateGroupSize();
      
      // Regenerate pagination segments for new group count
      this.generatePaginationSegments();
      
      // Snap to the nearest group boundary based on current position
      const currentGroup = this.getCurrentGroup();
      this.currentIndex = this.getGroupStartIndex(currentGroup);
      
      // Scroll to the new position without animation
      const slide = this.slides[this.currentIndex];
      if (slide) {
        slide.scrollIntoView({
          behavior: 'auto',
          inline: 'start',
          block: 'nearest'
        });
      }
    }
    
    this.update();
  }

  // Get the current group index based on slide index
  getCurrentGroup() {
    return Math.floor(this.currentIndex / this.groupSize);
  }

  // Get the starting slide index for a group
  getGroupStartIndex(groupIndex) {
    return groupIndex * this.groupSize;
  }

  goNext() {
    const currentGroup = this.getCurrentGroup();
    
    if (currentGroup < this.totalGroups - 1) {
      // Move to the start of the next group
      this.currentIndex = this.getGroupStartIndex(currentGroup + 1);
      this.scrollToIndex();
    }
  }

  goPrev() {
    const currentGroup = this.getCurrentGroup();
    
    if (currentGroup > 0) {
      // Move to the start of the previous group
      this.currentIndex = this.getGroupStartIndex(currentGroup - 1);
      this.scrollToIndex();
    }
  }

  scrollToIndex() {
    this.isProgrammaticScroll = true;

    const slide = this.slides[this.currentIndex];
    if (!slide) return;

    slide.scrollIntoView({
      behavior: 'smooth',
      inline: 'start',
      block: 'nearest'
    });

    this.update();

    // Clear any existing timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    // Wait for smooth scroll to complete
    this.scrollTimeout = setTimeout(() => {
      this.isProgrammaticScroll = false;
    }, 600);
  }

  onScroll() {
    if (this.isProgrammaticScroll) return;

    const scrollLeft = this.viewport.scrollLeft;
    const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
    
    // Calculate the current index with edge case handling
    let index;
    
    if (scrollLeft >= maxScroll - 5) {
      // At the end - set to last slide
      index = this.slides.length - 1;
    } else if (scrollLeft <= 5) {
      // At the beginning
      index = 0;
    } else {
      // Normal calculation
      const slideSize = this.slideWidth + this.gap;
      index = Math.round(scrollLeft / slideSize);
      // Clamp to valid range
      index = Math.max(0, Math.min(index, this.slides.length - 1));
    }

    if (index !== this.currentIndex) {
      this.currentIndex = index;
      this.update();
    }
  }

  update() {
    const currentGroup = this.getCurrentGroup();
    
    /* count */
    if (this.countEl) {
      this.countEl.textContent = `${currentGroup + 1}/${this.totalGroups}`;
    }

    /* progress */
    this.segments.forEach((seg, i) => {
      seg.classList.toggle('is-active', i === currentGroup);
    });

    /* arrows */
    this.prevBtn?.toggleAttribute('disabled', currentGroup === 0);
    this.nextBtn?.toggleAttribute(
      'disabled',
      currentGroup === this.totalGroups - 1
    );
  }
}

/* Init */
document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll('.section-ussf-player-carousel')
    .forEach(section => {
      new USSFPlayerCarousel(section);
    });
});